import { DependencyList, useCallback, useEffect } from "react";

import { tenantToTenantEventEmitter } from "@/common/eventEmmiters/tenantToTenantEventEmitter";
import { DataUpdatesChannelName } from "@/common/realtime/dataUpdatesChannelName";
import {
  DataUpdatesHubClientMethodName,
  EntityType,
  TenantToTenantCountersDto,
} from "@/core/api/generated/v1";
import * as tenantToTenantSlice from "@/store/tenantToTenant/tenantToTenantSlice";

import { useRealtimeDataUpdates } from "../../realtime/useRealtimeDataUpdates";
import { useAppSelector, useAppThunkDispatch } from "../../redux";
import { useTenantProfileV2 } from "../tenant/useTenantProfileV2";

export const useTenantToTenantCounters = (props?: {
  deps?: DependencyList;
}): TenantToTenantCountersDto | undefined => {
  const tenantProfile = useTenantProfileV2();
  const thunkDispatch = useAppThunkDispatch();

  const counters = useAppSelector((x) => x.tenantToTenant.counters);

  const refetchCounters = useCallback(() => {
    thunkDispatch(tenantToTenantSlice.getCountersThrottle());
  }, []);

  useEffect(() => {
    if (!counters) {
      refetchCounters();
    }
  }, [counters, ...(props?.deps || [])]);

  // refetch on local changes
  useEffect(() => {
    const subs = [
      tenantToTenantEventEmitter.on2("tenantConnectionRequestCreated", (params) => {
        refetchCounters();
      }),
      tenantToTenantEventEmitter.on2("tenantConnectionRequestUpdated", (params) => {
        refetchCounters();
      }),
      tenantToTenantEventEmitter.on2("tenantConnectionRequestDeleted", (params) => {
        refetchCounters();
      }),

      tenantToTenantEventEmitter.on2("tenantConnectionUpdated", (params) => {
        refetchCounters();
      }),
    ];
    return () => {
      subs.forEach((x) => x.off());
    };
  }, []);

  // refetch on remote changes
  useRealtimeDataUpdates({
    enabled: !!tenantProfile.tenantId,
    channelNames: [
      DataUpdatesChannelName.Entities(undefined, EntityType.TenantConnectionRequest),
      DataUpdatesChannelName.Entities(tenantProfile.tenantId, EntityType.TenantConnectionRequest),
      DataUpdatesChannelName.Entities(undefined, EntityType.TenantConnection),
      DataUpdatesChannelName.Entities(tenantProfile.tenantId, EntityType.TenantConnection),
      DataUpdatesChannelName.Entities(undefined, EntityType.TenantRequest),
      DataUpdatesChannelName.Entities(tenantProfile.tenantId, EntityType.TenantRequest),
    ],
    methodNames: [DataUpdatesHubClientMethodName.EntityChanged],
    handler: undefined,
    entityChangedHandler: (methodName, data) => {
      refetchCounters();
    },
  });

  return counters;
};
